/**
 * The minimum an item needs for archiving. Structural on purpose so this stays
 * importable from tests — entities pull in the data grid and cannot be loaded
 * under vitest.
 */
export type Archivable = { id: number; archived?: boolean };

/**
 * Split a set of selected IDs into the ones being archived and the ones being
 * brought back.
 *
 * One checkbox list covers both directions, so what a tick means depends on the
 * item's current state rather than on which list it was ticked in. IDs with no
 * matching item are dropped: the selection outlives a refetch, and an item
 * deleted in another tab must not turn into a write against a missing row.
 *
 * @param {Archivable[]} items every item that could be selected
 * @param {number[]} selectedIDs the ticked IDs
 * @returns {object} the items to archive and the items to restore
 */
export function partitionArchiveSelection<T extends Archivable>(
  items: T[],
  selectedIDs: number[]
): { toArchive: T[]; toRestore: T[] } {
  const byID = new Map(items.map((item) => [item.id, item]));

  const toArchive: T[] = [];
  const toRestore: T[] = [];

  selectedIDs.forEach((id) => {
    const item = byID.get(id);
    if (!item) return;
    if (item.archived) toRestore.push(item);
    else toArchive.push(item);
  });

  return { toArchive, toRestore };
}
